import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { Toolbar } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { JalaliDatePipe } from '@/pipes/jalali-date.pipe';
import { AthleteClassesService } from '@/services/athlete-classes.service';

@Component({
  selector: 'app-expiring-enrollments-report',
  standalone: true,
  imports: [CommonModule, FormsModule, TableModule, ButtonModule, Toolbar, ToastModule, JalaliDatePipe],
  providers: [MessageService],
  encapsulation: ViewEncapsulation.None,
  template: `
<p-toolbar class="mb-3">
  <ng-template #start>
    <div class="font-bold">گزارش‌ها</div>
  </ng-template>

  <ng-template #end>
    <div class="flex items-center gap-2">
      <span class="text-sm text-gray-600">انقضا تا</span>

      <input
        type="number"
        class="p-inputtext p-component w-20 text-center"
        [(ngModel)]="days"
        min="1"
        max="30"
      />

      <span class="text-sm text-gray-600">روز آینده</span>

      <p-button icon="pi pi-refresh" label="رفرش" severity="secondary" (onClick)="refreshAll()"></p-button>
    </div>
  </ng-template>
</p-toolbar>

<!-- ===================== GRID 1: Expiring ===================== -->
<div class="card mb-3">
  <div class="card-title">ورزشکارانی که تا {{ days }} روز آینده انقضا دارند</div>

  <p-table
    [value]="expiringLoading ? [] : expiringRows"
    [loading]="expiringLoading"
    stripedRows
    responsiveLayout="scroll"
    [paginator]="true"
    [rows]="15"
  >
    <ng-template pTemplate="header">
      <tr>
        <th style="width:70px">ردیف</th>
        <th>نام و نام خانوادگی</th>
        <th>نام کلاس</th>
        <th>تاریخ انقضا</th>
        <th style="width:220px">جلسات باقی‌مانده</th>
      </tr>
    </ng-template>

    <ng-template pTemplate="body" let-row let-i="rowIndex">
      <tr>
        <td>{{ i + 1 }}</td>
        <td class="font-bold">{{ row.fullName }}</td>
        <td>{{ row.classTitle }}</td>
        <td>{{ row.expiresAt ? (row.expiresAt | jalaliDate:'YYYY/MM/DD') : '-' }}</td>
        <td>
          <span class="pill">
            {{ row.remainingSessions }} از {{ row.totalSessions }}
          </span>
          <span class="text-xs text-gray-500 mr-2">(مصرف: {{ row.usedCount }})</span>
        </td>
      </tr>
    </ng-template>

    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="5" class="text-center text-sm text-gray-500 p-3">
          موردی یافت نشد.
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>

<!-- ===================== GRID 2: Class enroll counts ===================== -->
<div class="card">
  <div class="card-title">تعداد ورزشکار ثبت‌نام‌شده در هر کلاس</div>

  <div class="flex items-center gap-2 mb-2">
    <p-button
      [label]="onlyActiveClasses ? 'فقط کلاس‌های فعال: روشن' : 'فقط کلاس‌های فعال: خاموش'"
      severity="secondary"
      icon="pi pi-filter"
      (onClick)="toggleOnlyActiveClasses()"
    ></p-button>
  </div>

  <p-table
    [value]="countsLoading ? [] : classCounts"
    [loading]="countsLoading"
    stripedRows
    responsiveLayout="scroll"
    [paginator]="true"
    [rows]="15"
  >
    <ng-template pTemplate="header">
      <tr>
        <th style="width:70px">ردیف</th>
        <th>نام کلاس</th>
        <th>مربی</th>
        <th style="width:140px">ثبت‌نام کل</th>
        <th style="width:140px">ثبت‌نام فعال</th>
      </tr>
    </ng-template>

    <!-- ✅ اینجا حتماً let-row باشه -->
    <ng-template pTemplate="body" let-row let-i="rowIndex">
      <tr>
        <td>{{ i + 1 }}</td>
        <td class="font-bold">{{ row.classTitle }}</td>
        <td>{{ row.coachName || '-' }}</td>
        <td><span class="pill">{{ row.totalEnrollments }}</span></td>
        <td><span class="pill ok">{{ row.activeEnrollments }}</span></td>
      </tr>
    </ng-template>

    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="5" class="text-center text-sm text-gray-500 p-3">
          موردی یافت نشد.
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>

<p-toast position="top-left"></p-toast>
`,
  styles: [`
.card{ border:1px solid #e9ecef; border-radius:14px; padding:12px; background:#fff; }
.card-title{ font-weight:900; margin-bottom:10px; }
.pill{ display:inline-flex; align-items:center; justify-content:center; padding:4px 10px; border-radius:999px; background:#f1f3f5; font-weight:900; font-size:12px; }
.pill.ok{ background:#d3f9d8; color:#2b8a3e; }
`],
})
export class ExpiringEnrollmentsReportPage implements OnInit {
  days = 3;

  expiringLoading = false;
  expiringRows: any[] = [];

  countsLoading = false;
  classCounts: any[] = [];
  onlyActiveClasses = false;

  constructor(
    private athleteClassesService: AthleteClassesService,
    private messageService: MessageService,
  ) {}

  ngOnInit() {
    this.refreshAll();
  }

  refreshAll() {
    this.loadExpiring();
    this.loadCounts();
  }

  toggleOnlyActiveClasses() {
    this.onlyActiveClasses = !this.onlyActiveClasses;
    this.loadCounts();
  }

  loadExpiring() {
    this.expiringLoading = true;

    // ✅ اسم درست متد
    this.athleteClassesService.getExpiringEnrollments(this.days).subscribe({
      next: (res: any) => {
        this.expiringRows = res?.rows || [];
        this.expiringLoading = false;
      },
      error: (e: any) => {
        this.expiringRows = [];
        this.expiringLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'خطا',
          detail: e?.error?.message || 'خطا در دریافت لیست انقضا',
        });
      },
    });
  }

  loadCounts() {
    this.countsLoading = true;

    this.athleteClassesService.getClassEnrollCounts(this.onlyActiveClasses).subscribe({
      next: (rows: any[]) => {
        this.classCounts = rows || [];
        this.countsLoading = false;
      },
      error: (e: any) => {
        this.classCounts = [];
        this.countsLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'خطا',
          detail: e?.error?.message || 'خطا در دریافت تعداد ثبت‌نام کلاس‌ها',
        });
      },
    });
  }
}
